/* eslint-disable lingui/no-unlocalized-strings -- nomes de recurso, tipo e ação do fio, não texto de UI */
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import type { PermissionLevel, PermissionResource } from './permission-types';

/** Linha do catálogo: CRUD presente só onde o recurso admite; ações booleanas. */
export interface CatalogEntry {
  resource: PermissionResource;
  resourceDisplay: MessageDescriptor;
  readPermission?: PermissionLevel;
  createPermission?: PermissionLevel;
  updatePermission?: PermissionLevel;
  deletePermission?: PermissionLevel;
  actions?: Record<string, boolean>;
}

const crud = { createPermission: 'None', updatePermission: 'None', readPermission: 'None', deletePermission: 'None' } as const;
const off = (...names: string[]) => Object.fromEntries(names.map((name) => [name, false]));

/**
 * Espelho de `getDefaultPermissionList()` do legado (services/permissions/index.tsx). Recurso ou ação
 * que a API devolve e não está aqui é descartado; recurso novo no backend só aparece depois de entrar
 * aqui. Devolve objetos novos a cada chamada.
 */
export function getDefaultPermissionList(): CatalogEntry[] {
  return [
    {
      resource: 'CreditNote',
      resourceDisplay: msg`Operações de Crédito`,
      ...crud,
      actions: off(
        'SubmitApproval',
        'Clone',
        'AssigmentPreview',
        'AssignRecord',
        'Cancel',
        'ComplianceApproval',
        'CreditApproval',
        'DraftPreview',
        'InstrumentApproval',
        'LiquidationApproval',
        'LiquidationPaymentRevision',
        'ManualUpdate',
        'ReadDataSet',
        'RegisterFgtsSaqueAniversario',
        'RemoveBlock',
        'RequestDataSet',
        'RestoreRecord',
        'SendAssigmentEmail',
        'FinishSignature',
        'SendNotification',
        'SendToWedCred',
        'SetFund',
        'SignatureValidation',
        'UploadDocs',
        'AdminCancel',
        'ApproveWarranty',
        'ApproveWarrantyRevision',
        'ApproveMarginReserve',
        'RestartWorkflow',
        'CreditNoteOpInsurance',
        'VehicleInspectionApproval',
        'EditRelatedOperationLink',
      ),
    },
    { resource: 'User', resourceDisplay: msg`Usuários`, ...crud, actions: off('EnableUser', 'DisableUser') },
    { resource: 'UserGroup', resourceDisplay: msg`Grupo de Usuários`, ...crud },
    { resource: 'UserApplication', resourceDisplay: msg`Aplicações de Usuário`, ...crud },
    { resource: 'ResourceServer', resourceDisplay: msg`Servidor de Recurso`, ...crud },
    {
      resource: 'BankAccountRequest',
      resourceDisplay: msg`Solicitações de abertura de conta`,
      ...crud,
      actions: off('OpenBankAccount', 'ComplianceApproval', 'InstrumentApproval', 'SignatureValidation', 'UploadDocs'),
    },
    {
      // Sem Create/Delete no catálogo do legado (ver armadilha no SPEC: `hasPermission` devolve true).
      resource: 'BankAccount',
      resourceDisplay: msg`Contas Digitais`,
      readPermission: 'None',
      updatePermission: 'None',
      actions: off(
        'ViewOperators',
        'RequestNewOperator',
        'ViewPendingOperators',
        'AdminApproveOperator',
        'ViewAllBankAccounts',
        'RequestBankAccountLimit',
        'ApproveBankAccountLimit',
        'AdminApproveBeneficiary',
        'TerminateAccount',
        'ReactivateAccount',
        'AddAccountEntry',
        'AssignRecord',
        'MasterApproverAllBankAccounts',
        'SendToWalletManagement',
        'ManageAutomaticDebit',
        'ViewTransitoryAccount',
      ),
    },
    { resource: 'LegalPerson', resourceDisplay: msg`Pessoas Jurídicas`, ...crud, actions: off('RequestDataSet', 'ReadDataSet') },
    {
      resource: 'NaturalPerson',
      resourceDisplay: msg`Pessoas Físicas`,
      ...crud,
      actions: off('RequestDataSet', 'ReadDataSet', 'AuctionQuery', 'AuctionNotEligibleQuery', 'BaseOffEligibilityQuery'),
    },
    { resource: 'Person', resourceDisplay: msg`Pessoas`, readPermission: 'None' },
    { resource: 'Permission', resourceDisplay: msg`Permissões`, ...crud, actions: off('AllowUpdatePermissionsSameTenant') },
    { resource: 'Fund', resourceDisplay: msg`Cessionários`, ...crud },
    {
      resource: 'CreditProduct',
      resourceDisplay: msg`Produtos Crédito`,
      ...crud,
      actions: off('IncludeBeneficiaryProduct', 'ManageAutomaticDebit'),
    },
    { resource: 'BankAccountProduct', resourceDisplay: msg`Produtos Conta`, ...crud },
    { resource: 'BillingProduct', resourceDisplay: msg`Produtos Cobrança`, ...crud },
    { resource: 'BatchAssignment', resourceDisplay: msg`Cessão em Lote`, ...crud, actions: off('UploadDocs', 'AssignRecord') },
    { resource: 'PendingApprovals', resourceDisplay: msg`Central de Aprovação`, readPermission: 'None' },
    {
      resource: 'SignatureWorkFlow',
      resourceDisplay: msg`Processo de Assinatura`,
      ...crud,
      actions: off('StartSignatureWorkFlow', 'ResendNotificationSignatureWorkFlow', 'CancelSignatureWorkFlow'),
    },
    {
      resource: 'BillingAccount',
      resourceDisplay: msg`Gestão de Carteiras`,
      ...crud,
      actions: off('LiquidationApproval', 'UpdateBillingCosts', 'SendToWalletManagement'),
    },
    { resource: 'BillingWallet', resourceDisplay: msg`Convênios`, ...crud },
    { resource: 'ManagePayrollAgreementSettings', resourceDisplay: msg`Convênios de Crédito`, ...crud },
    {
      resource: 'DataQueryAuthorization',
      resourceDisplay: msg`Autorização Crédito Trabalhador`,
      ...crud,
      actions: off('TenantDataQueryAuthorization'),
    },
    {
      resource: 'InssMarginAuthorization',
      resourceDisplay: msg`Autorização Crédito INSS`,
      ...crud,
      actions: off('TenantInssMarginAuthorization'),
    },
    { resource: 'TenantRequest', resourceDisplay: msg`Parceiros`, ...crud },
    { resource: 'FrontWebSPI', resourceDisplay: msg`Gestão SPI`, ...crud },
    { resource: 'ChatbotSupport', resourceDisplay: msg`Suporte (Chatbot)`, ...crud },
    { resource: 'PixMed', resourceDisplay: msg`Pix Med Admin`, ...crud },
    { resource: 'Conciliation', resourceDisplay: msg`Conciliação`, ...crud },
    { resource: 'CreditProductRules', resourceDisplay: msg`Regras de produto`, ...crud, actions: off('TenantDataQueryAuthorization') },
    { resource: 'DimensaSubmission', resourceDisplay: msg`Envios Dimensa`, readPermission: 'None', updatePermission: 'None' },
    {
      // Sem Delete: acordo errado é cancelado, nunca apagado (comentário do legado).
      resource: 'CreditRecovery',
      resourceDisplay: msg`Recuperação de crédito`,
      createPermission: 'None',
      updatePermission: 'None',
      readPermission: 'None',
      actions: off('Cancel', 'AssignRecord'),
    },
    {
      resource: 'AgentConversation',
      resourceDisplay: msg`Assistente (Luy)`,
      readPermission: 'None',
      actions: off('Write', 'AgentDefinition:Read', 'AgentTool:Read'),
    },
  ];
}

/** Recursos que só existem com uma flag (`optionalResources` do legado). */
const optionalResources: Partial<Record<PermissionResource, (config: { CAIS_ENABLED?: number | null } | undefined) => boolean>> = {
  // U55: CAIS_ENABLED tratado como 1 por enquanto, para a permissão de conversa do Luy estar à vista no editor
  // (o painel já aparece para todos, U52). Para voltar à regra do legado, descomente a linha abaixo.
  // AgentConversation: (config) => config?.CAIS_ENABLED === 1,
};

/** Se o recurso aparece nas telas de edição de permissão; a checagem efetiva não filtra. */
export function isResourceAvailable(resource: PermissionResource, config?: { CAIS_ENABLED?: number | null }): boolean {
  const rule = optionalResources[resource];
  return !rule || rule(config);
}
